/**
 * DialerDerivedContext — Expose useDialerDerivedData en tant que context.
 *
 * 2026-06-04 (Fowler etape 9 migration) — Identique au pattern Telephony :
 * on enveloppe le hook `useDialerDerivedData` dans un Provider pour que
 * les pages externes (CallHistoryScreen, CRMScreen, FollowupsScreen...)
 * accedent aux vues derivees sans passer par la facade useDialer().
 *
 * Le Provider lit ses inputs depuis :
 *   - DialerData : prospectQueue, allCalls, activeCalls, selectedCustomerId
 *   - DialerSettings : parallelCallsCount, maxCallAttempts, maxCallAttemptsPeriod
 *
 * Zero duplication d'etat : la source de verite reste le hook pur.
 */

import React, { createContext, useContext } from 'react';
import {
  useDialerDerivedData,
  type DialerDerivedData,
} from '@/hooks/useDialerDerivedData';
import { useDialerData } from './DialerDataContext';
import { useDialerSettings } from './DialerSettingsContext';

const DialerDerivedContext = createContext<DialerDerivedData | null>(null);

export function DialerDerivedProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { prospectQueue, allCalls, activeCalls, selectedCustomerId } = useDialerData();
  const settings = useDialerSettings() as {
    parallelCallsCount?: number;
    maxCallAttempts?: number | 'unlimited';
    maxCallAttemptsPeriod?: 'day' | 'week' | 'month';
  };
  const value = useDialerDerivedData({
    prospectQueue,
    allCalls,
    activeCalls,
    selectedCustomerId,
    parallelCallsCount: settings.parallelCallsCount ?? 3,
    maxCallAttempts: settings.maxCallAttempts ?? 'unlimited',
    maxCallAttemptsPeriod: settings.maxCallAttemptsPeriod ?? 'day',
  });
  return (
    <DialerDerivedContext.Provider value={value}>
      {children}
    </DialerDerivedContext.Provider>
  );
}

export function useDialerDerived(): DialerDerivedData {
  const ctx = useContext(DialerDerivedContext);
  if (!ctx) {
    throw new Error('useDialerDerived must be used within <DialerDerivedProvider>');
  }
  return ctx;
}
